import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Filter } from "lucide-react";
import { Chip, Loading, Empty, SectionHeader, timeAgo, LoadError, usePlatformFetch } from "./_primitives";

const PREFIXES = [
  { value: "", label: "All" },
  { value: "auth.", label: "Auth" },
  { value: "project.", label: "Projects" },
  { value: "document.", label: "Documents" },
  { value: "sheet.", label: "Sheets" },
  { value: "share.", label: "Shares" },
  { value: "package.", label: "Packages" },
  { value: "intake.", label: "Intake" },
  { value: "ocr.", label: "OCR" },
  { value: "platform.", label: "Platform" },
];

export default function ActivityCenter() {
  const [prefix, setPrefix] = useState("");
  const { data, error, retry } = usePlatformFetch(`/platform/activity_feed?limit=150${prefix ? `&action_prefix=${encodeURIComponent(prefix)}` : ""}`);
  const rows = data?.rows || [];
  const loading = !data && !error;

  return (
    <div className="space-y-3" data-testid="activity-center">
      <div className="fop-surface-2 rounded-lg p-3 flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-3)]"><Filter size={12} /><span className="text-[10px] font-mono uppercase tracking-wider">Filter</span></div>
        {PREFIXES.map((p) => (
          <button key={p.value} onClick={() => setPrefix(p.value)} data-testid={`activity-filter-${p.value || "all"}`}
            className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded ${prefix === p.value ? "bg-[var(--fop-primary)] text-black" : "bg-[var(--fop-surface)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}>{p.label}</button>
        ))}
      </div>
      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="activity-feed">
        <SectionHeader icon={<Activity size={11} />} title={`Cross-tenant audit feed (${rows.length})`} />
        {error ? <LoadError error={error} retry={retry} label="Activity feed" />
          : loading ? <Loading /> : rows.length === 0 ? <Empty>No activity matches the filter.</Empty> : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">When</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Actor</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Action</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Target</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-[var(--fop-border)]" data-testid={`activity-row-${a.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--fop-text-4)]">{timeAgo(a.created_at)}</td>
                  <td className="px-3 py-1.5">{a.tenant_id ? <Link to={`/platform/tenants/${a.tenant_id}`} className="hover:text-[var(--fop-primary)] text-[11px]">{a.tenant_name}</Link> : <span className="text-[var(--fop-text-4)]">—</span>}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--fop-text-3)]">{a.actor_email || a.actor_id || "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-[var(--fop-text-2)]">{a.action}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--fop-text-4)]">{a.target_type ? `${a.target_type}:${(a.target_id || "").slice(0, 8)}` : "—"}</td>
                  <td className="px-3 py-1.5">
                    {a.success === false || a.severity === "error" || a.severity === "critical"
                      ? <Chip state="red" label={a.severity || "failed"} />
                      : <Chip state="ok" label="ok" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
